/**
 * professionalPageNormalizer.js — Normalização de dados da Página Profissional
 *
 * Converte os dados do formulário (react-hook-form) para o payload esperado
 * pela API: POST/PUT /api/pages
 *
 * Formato de saída:
 * {
 *   type: "professional",
 *   title: string,
 *   slug: string,
 *   templateId: string,
 *   config: { professionalId, displayName, ..., contact, socialLinks, booking,
 *             experience, services, testimonials, customStyles }
 * }
 */

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (mesmos padrões de pageDataNormalizer.js / socialLinksNormalizer.js)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Garante que a URL começa com https://
 */
export const ensureHttps = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Extrai domínio de uma URL (sem www.)
 * Ex: https://www.meusite.com.br → meusite.com.br
 */
export const extractDomain = (url) => {
  if (!url) return '';
  try {
    const domain = new URL(ensureHttps(url)).hostname;
    return domain.replace('www.', '');
  } catch {
    return url;
  }
};

/**
 * Normaliza telefone/whatsapp: garante prefixo +55 se o número não tiver código de país.
 * Aceita formatos como "11999999999", "(11) 99999-9999", "+5511999999999".
 * Se vazio, retorna ''.
 */
const normalizePhone = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  // Somente dígitos
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  // Já tem código de país
  if (trimmed.startsWith('+')) return trimmed;
  // Número com DDI brasileiro implícito (10 ou 11 dígitos → DDI 55)
  if (digits.length >= 10 && digits.length <= 11) return `+55${digits}`;
  // Outros: retornar como está
  return trimmed;
};

/**
 * Normaliza o objeto de redes sociais.
 * Aceita tanto { url: "..." } quanto string direta.
 * Remove entradas vazias.
 */
const normalizeSocialLinks = (socialLinks = {}) => {
  const KNOWN_PLATFORMS = [
    'instagram', 'linkedin', 'twitter', 'youtube',
    'facebook', 'tiktok', 'threads',
  ];

  return KNOWN_PLATFORMS.reduce((acc, platform) => {
    const raw = socialLinks?.[platform];
    const url = typeof raw === 'object' ? (raw?.url || '') : (raw || '');
    const normalized = ensureHttps(url.trim());
    if (normalized) {
      acc[platform] = { url: normalized };
    }
    return acc;
  }, {});
};

// ─────────────────────────────────────────────────────────────────────────────
// Normalizador principal
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normaliza dados do formulário da Página Profissional para o payload da API.
 *
 * @param {object} formData - Dados brutos do react-hook-form
 * @returns {object} Payload pronto para POST /api/pages
 */
export const normalizeProfessionalPageData = (formData = {}) => {
  // ── Campos de nível superior ──────────────────────────────────────────────
  const title      = (formData.displayName || '').trim();
  const slug       = (formData.slug || '').trim();
  const templateId = formData.templateId || null;

  // ── Contact ───────────────────────────────────────────────────────────────
  // Suporta tanto campos planos (formData['contact.phone']) quanto aninhados
  const rawContact = formData.contact || {};
  const contact = {
    phone:    normalizePhone(formData['contact.phone']    || rawContact.phone    || ''),
    whatsapp: normalizePhone(formData['contact.whatsapp'] || rawContact.whatsapp || ''),
    email:    (formData['contact.email']   || rawContact.email   || '').trim(),
    website:  ensureHttps(formData['contact.website'] || rawContact.website || ''),
  };
  // Remover website se vazio para não poluir o config
  if (!contact.website) delete contact.website;

  // ── Social Links ──────────────────────────────────────────────────────────
  const rawSocial = formData.socialLinks || {};
  // Também aceita campos planos como formData['socialLinks.instagram']
  const flatSocial = {
    instagram: formData['socialLinks.instagram'] || rawSocial.instagram,
    linkedin:  formData['socialLinks.linkedin']  || rawSocial.linkedin,
    twitter:   formData['socialLinks.twitter']   || rawSocial.twitter,
    youtube:   formData['socialLinks.youtube']   || rawSocial.youtube,
    facebook:  formData['socialLinks.facebook']  || rawSocial.facebook,
    tiktok:    formData['socialLinks.tiktok']    || rawSocial.tiktok,
    threads:   formData['socialLinks.threads']   || rawSocial.threads,
  };
  const socialLinks = normalizeSocialLinks(flatSocial);

  // ── Booking ───────────────────────────────────────────────────────────────
  const booking = {
    placeProfessionalIds: Array.isArray(formData.booking?.placeProfessionalIds)
      ? formData.booking.placeProfessionalIds.filter(Boolean)
      : [],
    showBookingSection: formData.booking?.showBookingSection !== false,
  };

  // ── Experience ────────────────────────────────────────────────────────────
  const experience = (formData.experience || [])
    .filter(item => (item?.title || '').trim())
    .map(item => ({
      title:       (item.title       || '').trim(),
      institution: (item.institution || '').trim(),
      yearStart:   item.yearStart ? Number(item.yearStart) : null,
      yearEnd:     item.yearEnd   ? (String(item.yearEnd).trim() || null) : null,
      description: (item.description || '').trim(),
    }));

  // ── Services ──────────────────────────────────────────────────────────────
  const services = (formData.services || [])
    .filter(item => (item?.name || '').trim())
    .map(item => ({
      name:        (item.name        || '').trim(),
      description: (item.description || '').trim(),
      duration:    (item.duration    || '').trim(),
      price:       (item.price       || '').trim(),
    }));

  // ── Testimonials ──────────────────────────────────────────────────────────
  const testimonials = (formData.testimonials || [])
    .filter(item => (item?.name || '').trim() && (item?.text || '').trim())
    .map(item => ({
      name:   (item.name   || '').trim(),
      text:   (item.text   || '').trim(),
      rating: item.rating  ? Number(item.rating) : null,
      date:   (item.date   || '').trim() || null,
    }));

  // ── Custom Styles ─────────────────────────────────────────────────────────
  const cs = formData.customStyles || {};
  const customStyles = {
    primaryColor:    (cs.primaryColor    || '#0d6efd').trim(),
    backgroundColor: (cs.backgroundColor || '#ffffff').trim(),
    textColor:       (cs.textColor       || '#212529').trim(),
    fontFamily:      (cs.fontFamily      || 'Inter').trim(),
    fontSize:        (cs.fontSize        || 'medium').trim(),
    backgroundImage: (cs.backgroundImage || '').trim(),
  };

  // ── Config completo ───────────────────────────────────────────────────────
  const config = {
    professionalId:     formData.professionalId     || null,
    displayName:        (formData.displayName        || '').trim(),
    title:              (formData.title              || '').trim(),
    specialty:          (formData.specialty          || '').trim(),
    registrationNumber: (formData.registrationNumber || '').trim(),
    aboutHtml:          (formData.aboutHtml          || '').trim(),
    photoUrl:           (formData.photoUrl           || ''),
    avatarShape:        (formData.avatarShape        || 'round'),
    contact,
    socialLinks,
    booking,
    experience,
    services,
    testimonials,
    customStyles,
  };

  return {
    type: 'professional',
    title,
    slug,
    templateId,
    config,
  };
};

/**
 * Alias para edição — mesma normalização (API não diferencia POST vs PUT).
 */
export const normalizeProfessionalPageDataForUpdate = normalizeProfessionalPageData;

export default normalizeProfessionalPageData;
